import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Search, SearchX, UserPlus, Users } from 'lucide-react';
import { readCache, writeCache } from '../../../services/localCache';
import { listMembers, age, MemberRow, Pagination } from '../api';
import EmptyState from '../../../components/EmptyState';
import PersonAvatar from '../../../components/PersonAvatar';
import MemberForm from '../components/MemberForm';
import { useAuth } from '../../../context/AuthContext';
import { api } from '../../../services/apiClient';
import { fechaCorta, haceDias, tonoFaltas } from '../components/AsistenciaResumen';

// (31-ago-2026) El selector de "rol" se RETIRÓ: esta lista es SOLO de alumnos (contacts);
// profesores y staff viven en sus propias pantallas (/profesores y /usuarios) y el backend
// nunca filtró por rol — elegir "Profesores" enseñaba las mismas alumnas. UI muerta fuera.

const STATUS = [
  { v: 'active', label: 'Activos' },
  { v: 'inactive', label: 'Inactivos' },
  { v: 'all', label: 'Todos' },
  // Quien vino a probar, quien paga clases sueltas y las ex-alumnas que Karla pasó a prospecto.
  // No cuentan como alumnas (así el número de arriba coincide con el de Kydemy) pero tampoco
  // desaparecen: aquí se ven, con su etiqueta.
  { v: 'lead', label: 'Prospectos' },
  // #1418 (Mabel 16-oct): alumnas activas con 3+ faltas seguidas desde su última asistencia, la
  // que más lleva primero. Sirve para detectar bajas. Criterio en lib/letsmoveFaltas.js (backend).
  { v: 'faltas', label: 'Con 3+ faltas seguidas' },
];

type FaltasRow = MemberRow & { faltas_seguidas?: number; faltas_mes?: number; ultima_asistencia?: string | null; dias_sin_venir?: number | null };
const sinAcentos = (t: string) => t.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

// Etiquetas de la vista de prospectos. El criterio vive en el backend (`lib/letsmovePersona.js`).
const TIPO: Record<string, { label: string; cls: string }> = {
  ex_alumna: { label: 'Ex-alumna', cls: 'bg-violet-500/15 text-violet-700 dark:text-violet-300' },
  visitante: { label: 'Clase suelta', cls: 'bg-sky-500/15 text-sky-700 dark:text-sky-300' },
  prospecto: { label: 'Prospecto', cls: 'bg-surface-2 text-content-3' },
};

function Avatar({ m, size = 'h-9 w-9' }: { m: MemberRow; size?: string }) {
  return <PersonAvatar contactId={m.account_id} hasPhoto={m.has_photo}
    name={m.firstname} lastName={m.lastname} size={size} />;
}

function MembershipBadge({ m }: { m: MemberRow }) {
  if (!m.membership_name) return <span className="text-content-3">—</span>;
  return <span className="inline-block rounded px-2 py-0.5 text-xs font-medium text-white" style={{ background: m.membership_color || '#64748b' }}>{m.membership_name}</span>;
}

function Estado({ m }: { m: FaltasRow }) {
  // Vista «faltas seguidas»: en vez de Activo, cuántas lleva y cuándo vino por última vez.
  if (m.faltas_seguidas != null) {
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5" data-testid="faltas-fila">
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tonoFaltas(m.faltas_seguidas)}`}>{m.faltas_seguidas} faltas seguidas</span>
        <span className="text-xs text-content-3">{m.ultima_asistencia ? `vino ${fechaCorta(m.ultima_asistencia)} (${haceDias(m.dias_sin_venir ?? 0)})` : 'nunca ha venido'}</span>
      </span>
    );
  }
  // Vista de prospectos: en vez de Activo/Inactivo (todos lo están), QUÉ clase de prospecto es.
  if (m.persona_tipo && m.persona_tipo !== 'alumna') {
    const t = TIPO[m.persona_tipo] || TIPO.prospecto;
    const n = Number(m.clases_sueltas || 0);
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5" data-testid="persona-tipo">
        <span className={`rounded px-2 py-0.5 text-xs font-medium ${t.cls}`}>{t.label}</span>
        {n > 0 && <span className="text-xs text-content-3">{n} {n === 1 ? 'clase suelta' : 'clases sueltas'}</span>}
      </span>
    );
  }
  // El único choque posible entre los dos sistemas: allá es prospecto y aquí está inscrita.
  if (m.prospecto_en_kydemy) {
    return <span className="rounded bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-700 dark:text-amber-300"
      title="En Kydemy esta persona figura como prospecto, pero está inscrita a una clase vigente. Conviene aclararlo."
      data-testid="prospecto-en-kydemy">Prospecto en Kydemy</span>;
  }
  return m.active
    ? <span className="inline-flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400"><span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />Activo</span>
    : <span className="text-content-3">Inactivo</span>;
}

export default function MembersListPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Pinta al instante la vista default (alumnos activos, pág. 1) desde caché local
  // y refresca por detrás — mismo patrón stale-while-revalidate del Home.
  const cached = readCache<{ rows: MemberRow[]; pg: Pagination }>('members-list');
  const [rows, setRows] = useState<FaltasRow[]>(cached?.rows ?? []);
  const [pg, setPg] = useState<Pagination>(cached?.pg ?? { page: 1, page_size: 20, total: 0 });
  // ?buscar= viene del buscador global de la cabecera ("Ver todos los resultados");
  // con término se arranca en estado "all" para que también aparezcan inactivas.
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('buscar') || '');
  const [status, setStatus] = useState(() => new URLSearchParams(window.location.search).get('buscar') ? 'all' : 'active');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(!cached);
  const firstRun = useRef(true);
  const [showForm, setShowForm] = useState(false);
  const [params, setParams] = useSearchParams();

  // Atajo del FAB móvil: /alumnos?nuevo=1 → abre "Nuevo alumno".
  useEffect(() => {
    if (params.get('nuevo') === '1') {
      setShowForm(true);
      params.delete('nuevo');
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  // 🚨 «Ver todos los resultados» del buscador global (Carlos 15-ago): manda a
  // /alumnos?buscar=fernanda, pero el término sólo se leía en el useState inicial. Si YA estabas
  // en /alumnos, React Router no vuelve a montar la página → el término se ignoraba y salía la
  // lista completa. Ahora se aplica cada vez que llega el parámetro, y se limpia después (mismo
  // patrón que ?nuevo=1) para que el buscador global funcione dos veces seguidas.
  useEffect(() => {
    const t = params.get('buscar');
    if (t == null) return;
    setSearch(t);
    setStatus('all');      // con término se buscan también las cuentas inactivas
    setPage(1);
    params.delete('buscar');
    setParams(params, { replace: true });
  }, [params, setParams]);

  useEffect(() => {
    // La 1ª carga va SIN debounce (el retraso solo tiene sentido al teclear la
    // búsqueda) y sin skeleton si ya pintamos desde caché.
    const immediate = firstRun.current;
    firstRun.current = false;
    if (!immediate || rows.length === 0) setLoading(true);
    if (status === 'faltas') {
      // Una sola consulta agregada en el backend; la búsqueda filtra en memoria (son pocas).
      const ck = 'members-faltas';
      const pinta = (all: FaltasRow[]) => {
        const q = sinAcentos(search.trim());
        const f = q ? all.filter((m) => sinAcentos(`${m.firstname} ${m.lastname || ''} ${m.phone_number || ''}`).includes(q)) : all;
        setRows(f); setPg({ page: 1, page_size: Math.max(1, f.length), total: f.length });
      };
      const c = readCache<FaltasRow[]>(ck);
      if (c) { pinta(c); setLoading(false); }
      api.get<{ data: FaltasRow[] }>('/members/faltas?min=3')
        .then((r) => { writeCache(ck, r.data); pinta(r.data); })
        .catch(() => { if (!c) { setRows([]); setPg({ page: 1, page_size: 20, total: 0 }); } })
        .finally(() => setLoading(false));
      return;
    }
    const t = setTimeout(() => {
      listMembers({ search, status, page })
        .then((r) => {
          setRows(r.data); setPg(r.pagination);
          if (!search && status === 'active' && page === 1) {
            writeCache('members-list', { rows: r.data, pg: r.pagination });
          }
        })
        .finally(() => setLoading(false));
    }, immediate ? 0 : 250); // debounce búsqueda
    return () => clearTimeout(t);
  }, [search, status, page]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalPages = Math.max(1, Math.ceil(pg.total / pg.page_size));
  const open = (id: number) => navigate(`/alumnos/${id}`);

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-content"><Users className="text-brand-blue" size={24} /> {status === 'lead' ? 'Prospectos' : status === 'faltas' ? 'Alumnos con faltas' : 'Alumnos'} <span className="font-normal text-content-3">({pg.total})</span></h2>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-content-3" />
            <input
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
              placeholder="Buscar nombre o teléfono…"
              className="w-full rounded-lg border border-line bg-surface py-2 pl-9 pr-3 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30 sm:w-64"
            />
          </div>
          <select
            value={status}
            onChange={(e) => { setPage(1); setStatus(e.target.value); }}
            className="shrink-0 rounded-lg border border-line bg-surface px-2 py-2 text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/30"
          >
            {STATUS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
          {user?.role === 'admin' && (
            <button
              onClick={() => setShowForm(true)}
              className="flex shrink-0 items-center gap-1.5 rounded-lg bg-accent-solid px-3 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90"
            >
              <UserPlus size={16} /> <span className="hidden sm:inline">Nuevo alumno</span>
            </button>
          )}
        </div>
      </div>

      {status === 'faltas' && (
        <p className="mb-3 text-sm text-content-2" data-testid="faltas-ayuda">
          Alumnas activas con 3 o más faltas seguidas desde su última asistencia (ámbar 3-4, rojo 5 o más). Falta = clase a la que
          está inscrita, con lista tomada, en la que no aparece como asistente; las clases sin lista no cuentan.
        </p>
      )}

      {showForm && (
        <MemberForm
          onClose={() => setShowForm(false)}
          onCreated={(id) => { setShowForm(false); navigate(`/alumnos/${id}`); }}
        />
      )}

      {/* ===== Móvil: tarjetas apiladas ===== */}
      <div className="space-y-2 md:hidden">
        {loading && Array.from({ length: 8 }).map((_, i) => (
          <div key={`mk-${i}`} className="flex items-center gap-3 rounded-xl border border-line bg-surface p-3">
            <div className="h-11 w-11 animate-pulse rounded-full bg-skeleton" />
            <div className="flex-1 space-y-2"><div className="h-4 w-1/2 animate-pulse rounded bg-skeleton" /><div className="h-3 w-2/3 animate-pulse rounded bg-skeleton" /></div>
          </div>
        ))}
        {!loading && rows.length === 0 && (
          <div className="rounded-xl border border-line bg-surface">
            <EmptyState compact icon={SearchX} title="Sin resultados" subtitle="Prueba con otro nombre, teléfono o filtro." />
          </div>
        )}
        {!loading && rows.map((m) => (
          <button key={m.account_id} onClick={() => open(m.account_id)}
            className="flex w-full items-center gap-3 rounded-xl border border-line bg-surface p-3 text-left transition active:bg-surface-2">
            <Avatar m={m} size="h-11 w-11" />
            <div className="min-w-0 flex-1">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate font-medium text-content">{m.firstname} {m.lastname}</span>
                {m.faltas_seguidas == null && <span className="shrink-0 text-xs"><Estado m={m} /></span>}
              </div>
              {/* Vista de faltas: el chip va en su propio renglón (al lado del nombre lo truncaba). */}
              {m.faltas_seguidas != null && <div className="mt-1"><Estado m={m} /></div>}
              <div className="mt-0.5 truncate text-xs text-content-3">
                {age(m.dob)} años{m.phone_number ? ` · ${m.phone_number}` : ''}
              </div>
              <div className="mt-1.5"><MembershipBadge m={m} /></div>
            </div>
          </button>
        ))}
      </div>

      {/* ===== Escritorio: tabla ===== */}
      <div className="hidden overflow-hidden rounded-xl border border-line bg-surface shadow-sm md:block">
        <table className="w-full text-sm">
          <thead className="border-b border-line bg-surface-2 text-content-3">
            <tr>
              <th className="px-4 py-2.5 text-left font-medium"> </th>
              <th className="px-4 py-2.5 text-left font-medium">Nombre</th>
              <th className="px-4 py-2.5 text-left font-medium">Edad</th>
              <th className="px-4 py-2.5 text-left font-medium">Teléfono</th>
              <th className="px-4 py-2.5 text-left font-medium">Mensualidad</th>
              <th className="px-4 py-2.5 text-left font-medium">{status === 'lead' ? 'Tipo' : 'Estado'}</th>
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 8 }).map((_, i) => (
              <tr key={`sk-${i}`} className="border-t border-line">
                <td className="px-4 py-3"><div className="h-9 w-9 animate-pulse rounded-full bg-skeleton" /></td>
                <td className="px-4 py-3" colSpan={5}><div className="h-4 w-2/3 animate-pulse rounded bg-skeleton" /></td>
              </tr>
            ))}
            {!loading && rows.length === 0 && <tr><td colSpan={6}><EmptyState icon={SearchX} title="Sin resultados" subtitle="Prueba con otro nombre, teléfono o filtro." /></td></tr>}
            {!loading && rows.map((m) => (
              <tr
                key={m.account_id}
                onClick={() => open(m.account_id)}
                className="cursor-pointer border-t border-line transition even:bg-surface-2/40 hover:bg-surface-2"
              >
                <td className="px-4 py-2"><Avatar m={m} /></td>
                <td className="px-4 py-2 font-medium text-content">{m.firstname} {m.lastname}</td>
                <td className="px-4 py-2 text-content-2">{age(m.dob)}</td>
                <td className="px-4 py-2 text-content-2">{m.phone_number || '—'}</td>
                <td className="px-4 py-2"><MembershipBadge m={m} /></td>
                <td className="px-4 py-2"><Estado m={m} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between text-sm text-content-2">
        <span>Página {pg.page} de {totalPages}</span>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
            className="rounded-lg border border-line bg-surface px-3 py-1.5 transition hover:bg-surface-2 disabled:opacity-40">Anterior</button>
          <button disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
            className="rounded-lg border border-line bg-surface px-3 py-1.5 transition hover:bg-surface-2 disabled:opacity-40">Siguiente</button>
        </div>
      </div>
    </div>
  );
}
